import { Box, CircularProgress } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { PageLayout, RobotoTypography } from "@/shared/components";
import {
  parseRobotoUri,
  RobotoUriType,
} from "@/shared/components/RobotoMarkdown";
import { IngestionStatus } from "@/shared/domain/files";
import { useFile } from "@/shared/domain/files/hooks";
import { useMessagePathById } from "@/shared/domain/topics/hooks/useMessagePath";
import { useTopicById } from "@/shared/domain/topics/hooks/useTopic";
import { getWorkspaceInput } from "@/shared/domain/workspaces/WorkspaceBuilder";
import { Goto } from "@/shared/navigation/Goto";
import { layoutNavigationState } from "@/shared/navigation/layoutNavigation";
import { determineDefaultPanelType } from "@/shared/state/visualization/schema/v1";
import { nanoSecToSecStr } from "@/shared/time";

interface NotFoundError {
  type: RobotoUriType;
  id: string;
}

/**
 * Redirect page for roboto:// URIs.
 *
 * This page accepts a `uri` query parameter containing a URL-encoded roboto:// URI
 * and redirects to the appropriate page in the application.
 *
 * Example URLs:
 * - /open?uri=roboto%3A%2F%2Fdataset%2Fds_123
 * - /open?uri=roboto%3A%2F%2Ffile%2Ffl_123%3Ft%3D1740099243000000000
 * - /open?uri=roboto%3A%2F%2Ftopic%2Ftp_123
 * - /open?uri=roboto%3A%2F%2Fmsgpath%2Fmp_123%3Ft%3D1740099243000000000
 */
export function RobotoUriRedirect() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [notFoundError, setNotFoundError] = useState<NotFoundError | null>(
    null,
  );

  const uri = searchParams.get("uri");
  const parsed = useMemo(() => (uri ? parseRobotoUri(uri) : undefined), [uri]);

  // Fetch data for topics and message paths to construct proper visualizer URLs
  const messagePath = useMessagePathById(
    parsed?.type === "msgpath" ? parsed.id : undefined,
  );
  const topic = useTopicById(parsed?.type === "topic" ? parsed.id : undefined);
  const messagePathTopic = useTopicById(messagePath.data?.topic_id);

  // For files, we need to check ingestion status to determine the redirect target
  const file = useFile(parsed?.type === "file" ? parsed.id : undefined);

  useEffect(() => {
    if (!parsed) {
      return;
    }

    const { type, id, t } = parsed;

    // Build the redirect URL based on resource type
    switch (type) {
      case "dataset": {
        navigate(Goto.createDatasetRoute(id), { replace: true });
        return;
      }

      case "file": {
        // Wait for file data to determine redirect target
        if (!file.data && !file.error) {
          return; // Still loading
        }

        if (file.error || !file.data) {
          // File not found or error - redirect to file page anyway
          const params = new URLSearchParams();
          if (t !== undefined) {
            params.set("t", nanoSecToSecStr(t));
          }
          const queryString = params.toString();
          navigate(
            `${Goto.route.Files}/${id}${queryString ? `?${queryString}` : ""}`,
            { replace: true },
          );
          return;
        }

        // If file is ingested, go to visualizer; otherwise go to file page
        if (file.data.ingestion_status !== IngestionStatus.NotIngested) {
          const params = new URLSearchParams({ file: id });
          if (t !== undefined) {
            params.set("t", nanoSecToSecStr(t));
          }
          navigate(`${Goto.route.Visualize}?${params}`, { replace: true });
        } else {
          const params = new URLSearchParams();
          if (t !== undefined) {
            params.set("t", nanoSecToSecStr(t));
          }
          const queryString = params.toString();
          navigate(
            `${Goto.route.Files}/${id}${queryString ? `?${queryString}` : ""}`,
            { replace: true },
          );
        }
        return;
      }

      case "topic": {
        // Wait for topic data to get the file ID
        if (!topic.data && !topic.error) {
          return; // Still loading
        }

        if (topic.error || !topic.data) {
          // Topic not found - show error
          setNotFoundError({ type: "topic", id });
          return;
        }

        const params = new URLSearchParams({
          file: topic.data.association.association_id,
        });
        if (t !== undefined) {
          params.set("t", nanoSecToSecStr(t));
        }
        navigate(`${Goto.route.Visualize}?${params}`, { replace: true });
        return;
      }

      case "msgpath": {
        // Wait for message path and its topic data
        if (
          (!messagePath.data && !messagePath.error) ||
          (!messagePathTopic.data &&
            !messagePathTopic.error &&
            messagePath.data)
        ) {
          return; // Still loading
        }

        if (messagePath.error || !messagePath.data) {
          // Message path not found - show error
          setNotFoundError({ type: "msgpath", id });
          return;
        }

        if (messagePathTopic.error || !messagePathTopic.data) {
          // Topic for message path not found - show error
          setNotFoundError({ type: "topic", id: messagePath.data.topic_id });
          return;
        }

        const panelType = determineDefaultPanelType(
          messagePath.data.canonical_data_type,
        );
        const layout = getWorkspaceInput(
          panelType,
          messagePathTopic.data,
          messagePath.data,
        );

        const params = new URLSearchParams({
          file: messagePathTopic.data.association.association_id,
        });
        if (t !== undefined) {
          params.set("t", nanoSecToSecStr(t));
        }
        // Set layout in session storage for the visualizer to pick up
        layoutNavigationState.set(layout);

        navigate(`${Goto.route.Visualize}?${params}`, { replace: true });
        return;
      }
    }
  }, [
    parsed,
    navigate,
    topic.data,
    topic.error,
    messagePath.data,
    messagePath.error,
    messagePathTopic.data,
    messagePathTopic.error,
    file.data,
    file.error,
  ]);

  // Show error if no URI provided or invalid
  if (!uri) {
    return (
      <PageLayout title="Roboto - Redirect">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
          textAlign="center"
          gap={2}
        >
          <RobotoTypography variant="h4">Missing URI</RobotoTypography>
          <RobotoTypography variant="body1">
            No roboto:// URI was provided in the request.
          </RobotoTypography>
        </Box>
      </PageLayout>
    );
  }

  if (!parsed) {
    return (
      <PageLayout title="Roboto - Redirect">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
          textAlign="center"
          gap={2}
        >
          <RobotoTypography variant="h4">Invalid URI</RobotoTypography>
          <RobotoTypography variant="body1">
            The provided URI could not be parsed: {uri}
          </RobotoTypography>
        </Box>
      </PageLayout>
    );
  }

  // Show not found error for topics and message paths
  if (notFoundError) {
    const resourceLabel =
      notFoundError.type === "topic" ? "Topic" : "Message Path";
    return (
      <PageLayout title="Roboto - Not Found">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
          textAlign="center"
          gap={2}
        >
          <RobotoTypography variant="h4">
            {resourceLabel} Not Found
          </RobotoTypography>
          <RobotoTypography variant="body1">
            The {resourceLabel.toLowerCase()} with ID &quot;{notFoundError.id}
            &quot; could not be found.
          </RobotoTypography>
        </Box>
      </PageLayout>
    );
  }

  // Show loading state while redirecting
  return (
    <PageLayout title="Roboto - Redirect">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        gap={2}
      >
        <CircularProgress />
        <RobotoTypography variant="body1">Redirecting...</RobotoTypography>
      </Box>
    </PageLayout>
  );
}
